import type {List} from 'immutable';

import {addGenericErrorToast} from '@albert-io/atomic';
import type {ClassroomModelV1} from 'src/resources/augmented/Classroom/ClassroomModel.v1';
import getAssignmentsFilters from '../Assignments/getFilters';
import {getClassroomsQuery} from 'client/Classrooms/StudentDashboard/StudentDashboard.queries.js';
import getLazyQueryData from 'apollo/getLazyQueryData';
import getShouldRedirectUser from '../redirect/getShouldRedirectUser';
import getToDoSkillsQueryVariables from '../SkillPath/getQueryVariables';
import getUserDefaultRoute from '../redirect/getUserDefaultRoute';
import {history} from 'client/history';
import isBaseRoute from '../isBaseRoute';
import sessionStore from 'client/Session/SessionStore';
import type {StudentAssignmentsQueryHookResult} from '../Assignments/queries/StudentAssignments.queries.generated';
import {useStudentAssignmentsLazyQuery} from '../Assignments/queries/StudentAssignments.queries.generated';
import {useStudentHomepageStore} from '../useStudentHomepageStore';
import {useToDoSkillsLazyQuery} from '../SkillPath/queries/StudentSkillPath.queries.generated';
import {Routes} from '../routes.constants';
import {useShallow} from 'zustand/react/shallow';

/**
 * Fetches the current user's classrooms, to-do assignments, and to-do skill paths, then updates the user's default route
 * based on those resources. Optionally redirects the user to that default route after the default route has been determined.
 */
export default function useSyncDefaultRoute() {
  const studentId = sessionStore.getUserId();
  const {setUserDefaultRoute, setClassrooms, setAreClassroomsLoading} = useStudentHomepageStore(
    useShallow((state) => ({
      setUserDefaultRoute: state.setUserDefaultRoute,
      setClassrooms: state.setClassrooms,
      setAreClassroomsLoading: state.setAreClassroomsLoading
    }))
  );

  const assignmentsFilters = getAssignmentsFilters(undefined, 'toDo');
  const [fetchStudentAssignments] = useStudentAssignmentsLazyQuery({
    variables: {filters: assignmentsFilters}
  });

  const [fetchToDoSkills] = useToDoSkillsLazyQuery();

  async function syncDefaultRoute(
    classroomId: string | undefined,
    pathname: string,
    skipRedirect = false
  ) {
    setUserDefaultRoute(null);

    // The user can navigate elsewhere while the fetches are in flight — never redirect
    // based on a pathname they have already left.
    const replaceIfStillOnPage = (route: string) => {
      if (!skipRedirect && window.location.pathname === pathname) {
        history.replace(route);
      }
    };

    let classrooms: List<ClassroomModelV1> | null = null;

    try {
      const [fetchedToDoAssignments, fetchedClassrooms]: [
        StudentAssignmentsQueryHookResult,
        List<ClassroomModelV1>
      ] = await Promise.all([
        fetchStudentAssignments(),
        getClassroomsQuery(studentId).getResourcePromise()
      ]);

      classrooms = fetchedClassrooms;
      setClassrooms(fetchedClassrooms);
      setAreClassroomsLoading(false);

      const toDoAssignments =
        getLazyQueryData(fetchedToDoAssignments).currentUser?.studentAssignments.results ?? [];

      const toDoSkillsQueryVariables = getToDoSkillsQueryVariables(
        undefined,
        fetchedClassrooms.map((fetchedClassroom) => fetchedClassroom.getId()).toArray()
      );

      const fetchedToDoSkills = await fetchToDoSkills(toDoSkillsQueryVariables);
      const toDoSkills = getLazyQueryData(fetchedToDoSkills).subjects?.results ?? [];

      const userDefaultRoute = getUserDefaultRoute({classrooms, toDoAssignments, toDoSkills});

      setUserDefaultRoute(userDefaultRoute);

      if (getShouldRedirectUser({classroomId, classrooms, pathname})) {
        replaceIfStillOnPage(userDefaultRoute);
      }
    } catch (error) {
      console.error(error);

      if (classrooms !== null) {
        // The classrooms fetch succeeded, so membership is known — apply the normal
        // redirect policy with the to-do data degraded to empty. We don't toast here: the
        // failed to-do query is a route-decision probe that the tab needing it re-fetches
        // and surfaces itself, so toasting would double up on that tab's own error.
        const fallbackRoute = getUserDefaultRoute({
          classrooms,
          toDoAssignments: [],
          toDoSkills: []
        });

        setUserDefaultRoute(fallbackRoute);

        if (getShouldRedirectUser({classroomId, classrooms, pathname})) {
          replaceIfStillOnPage(fallbackRoute);
        }
      } else {
        // The classrooms fetch itself failed. Mandark never TTL-refreshes error-status
        // interest entries, so re-dirty the classrooms query — otherwise every later /home
        // mount is served the failure as "no classes" and strands an enrolled student on
        // the welcome screen until a hard reload.
        getClassroomsQuery(studentId).invalidateInterest();
        // Nothing else re-surfaces the classrooms failure, so toast. Membership is unknown,
        // so only move users who can't stay put: the bare /home route renders no tab content.
        addGenericErrorToast();
        setUserDefaultRoute(Routes.Assignments);

        if (isBaseRoute(pathname)) {
          replaceIfStillOnPage(Routes.Assignments);
        }
      }
    }
  }

  return {syncDefaultRoute};
}
